import React from "react";
import { logError } from ".";
import { isProduction, isDevelopment } from "../util/ContextHelper";

interface ErrorBoundaryProps
{
    className?: string;

    /**
     * Swallows error messages
     */
    silent?: boolean;
}

interface ErrorBoundaryState
{
    error?: Error;
    errorInfo?: React.ErrorInfo;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState>
{
    constructor(props)
    {
        super(props);
        this.state = { error: undefined, errorInfo: undefined };
    }

    /**
     * Catches any errors thrown during react rendering. Does not work with react.renderToString()
     * @param error Error thrown
     * @param errorInfo {React.ErrorInfo} React Error information and trace
     */
    componentDidCatch(error: Error, errorInfo: React.ErrorInfo)
    {
        this.setState({
            error: error,
            errorInfo: errorInfo
        });

        logError(error.message, errorInfo);
    }

    render()
    {
        /**
         * react.renderToString does not support componentDidCatch. Render must be wrapped in try-catch
         */
        try
        {
            if (this.state.error || this.state.errorInfo)
            {
                return this.renderError();
            }

            return this.props.children;
        }
        catch (error)
        {
            this.setState({
                error: error
            });

            // Catch any custom errors thrown server-side
            logError(error.message, error);

            return this.renderError();
        }
    }

    renderError()
    {
        if (this.state.error || this.state.errorInfo)
        {
            if (this.props.silent && isProduction())
            {
                return null;
            }

            // You can render any custom fallback UI
            return (
                <div className={this.props.className}>
                    <h4>Something went wrong.</h4>
                    {isDevelopment() ?
                        <details style={{ whiteSpace: "pre-wrap", textAlign: "left" }}>
                            {this.state.error && this.state.error.toString()}
                            <br />
                            {this.state.errorInfo?.componentStack}
                        </details>
                        : null}
                </div>
            );
        }

        return null;
    }
}

export default ErrorBoundary;